/**
 * Review Details Page
 * Full review interface with comments, scorecard, decision
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewDetails = () => {
  const { reviewId } = useParams();
  const [review, setReview] = useState(null);
  const [comments, setComments] = useState([]);
  const [scorecard, setScorecard] = useState(null);
  const [newComment, setNewComment] = useState({ section: '', comment: '', comment_type: 'question', severity: 'normal' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (reviewId) {
      fetchReview();
      fetchComments();
      fetchScorecard();
    }
  }, [reviewId]);

  const fetchReview = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReview(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/review-comments/review/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchScorecard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/review-scorecard/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScorecard(response.data);
    } catch (error) {
      // Scorecard might not exist yet
      console.log('No scorecard yet');
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/review-comments`, {
        review_id: reviewId,
        ...newComment,
        requires_response: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewComment({ section: '', comment: '', comment_type: 'question', severity: 'normal' });
      fetchComments();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const updateScorecard = async (scores) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/review-scorecard/${reviewId}`, scores, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchScorecard();
      alert('Scorecard updated');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) return <div className="p-8">Loading review...</div>;
  if (!review) return <div className="p-8">Review not found</div>;

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Review: {review.plan_name || 'Untitled Plan'}</h1>
        <div className="flex gap-4 mt-2 text-sm text-gray-600">
          <span>Client: {review.client_name || 'N/A'}</span>
          <span>Submitted: {new Date(review.submitted_at).toLocaleDateString()}</span>
          <span className="font-semibold">Loan Request: ETB {(review.loan_amount_requested / 1000000).toFixed(1)}M</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Comments Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">Comments ({comments.length})</h2>
            
            {/* Add Comment Form */}
            <form onSubmit={addComment} className="mb-6 p-4 bg-gray-50 rounded">
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Section</label>
                <select
                  value={newComment.section}
                  onChange={(e) => setNewComment({...newComment, section: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">General</option>
                  <option value="executive_summary">Executive Summary</option>
                  <option value="financials">Financial Projections</option>
                  <option value="market_analysis">Market Analysis</option>
                  <option value="risk_assessment">Risk Assessment</option>
                </select>
              </div>
              <div className="mb-3">
                <textarea
                  value={newComment.comment}
                  onChange={(e) => setNewComment({...newComment, comment: e.target.value})}
                  className="w-full border rounded px-3 py-2 h-24"
                  placeholder="Add your comment or question..."
                  required
                />
              </div>
              <div className="flex gap-2">
                <select
                  value={newComment.comment_type}
                  onChange={(e) => setNewComment({...newComment, comment_type: e.target.value})}
                  className="border rounded px-3 py-1 text-sm"
                >
                  <option value="question">Question</option>
                  <option value="clarification">Clarification</option>
                  <option value="concern">Concern</option>
                  <option value="suggestion">Suggestion</option>
                </select>
                <button type="submit" className="bg-blue-600 text-white px-4 py-1 rounded hover:bg-blue-700">
                  Add Comment
                </button>
              </div>
            </form>

            {/* Comments List */}
            <div className="space-y-3">
              {comments.map(c => (
                <div key={c.id} className="border-l-4 border-blue-500 bg-gray-50 p-4 rounded">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="font-medium">{c.user_name || 'Reviewer'}</span>
                      <span className="text-xs text-gray-500 ml-2">({c.user_type})</span>
                      {c.section && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{c.section}</span>}
                    </div>
                    <span className="text-xs text-gray-500">{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-gray-700">{c.comment}</p>
                  {!c.resolved && (
                    <div className="mt-2">
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Awaiting Response</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Scorecard */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold mb-4">Evaluation Scorecard</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Management (/30)</label>
                <input
                  type="number"
                  min="0"
                  max="30"
                  defaultValue={scorecard?.management_score || 0}
                  onBlur={(e) => updateScorecard({ management_score: parseInt(e.target.value) })}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Market (/25)</label>
                <input
                  type="number"
                  min="0"
                  max="25"
                  defaultValue={scorecard?.market_score || 0}
                  onBlur={(e) => updateScorecard({ market_score: parseInt(e.target.value) })}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Financial (/30)</label>
                <input
                  type="number"
                  min="0"
                  max="30"
                  defaultValue={scorecard?.financial_score || 0}
                  onBlur={(e) => updateScorecard({ financial_score: parseInt(e.target.value) })}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Risk (/15)</label>
                <input
                  type="number"
                  min="0"
                  max="15"
                  defaultValue={scorecard?.risk_score || 0}
                  onBlur={(e) => updateScorecard({ risk_score: parseInt(e.target.value) })}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              <div className="pt-4 border-t">
                <div className="flex justify-between items-center">
                  <span className="font-bold">Total Score:</span>
                  <span className="text-2xl font-bold text-blue-600">
                    {(scorecard?.management_score || 0) + (scorecard?.market_score || 0) + 
                     (scorecard?.financial_score || 0) + (scorecard?.risk_score || 0)}/100
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Review Status */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold mb-4">Review Status</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className="font-medium capitalize">{review.status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Comments:</span>
                <span className="font-medium">{review.total_comments || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Unresolved:</span>
                <span className="font-medium text-yellow-600">{review.unresolved_comments || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewDetails;